<template>
	<div id="app">
		<Message
			v-if="error.message.length > 0"
			:message="error.message"
			@close="EventBus.$emit('error', {message: ''})"/>

		<Loading v-if="appState === AppState.LOADING"></Loading>
		<template v-else>
			<Maintenance v-if="appState === AppState.DISABLED"></Maintenance>
			<router-view :config="config" :appState="appState" :owner="owner" v-else></router-view>
		</template>
	</div>
</template>

<script>
	import Vue from 'vue'
	import axios from 'axios'
	import { EventBus } from './event-bus'
	import { AppState } from './app-state';
	import Message from './components/Message'
	import Loading from './components/page/Loading'
	import Maintenance from './components/page/Maintenance'

	export default {
		name : 'app',
		components: {
			Message, Loading, Maintenance
		},
		data () {
			return {
				/**
				 * An artist/venue entity object in the format provided by
				 * the search serializers
				 */
				owner : null,
				error: {
					message: ''
				},
				config: {
					bundles: []
				},
				/**
				 * Options:
				 * * 'loading'
				 * * 'enabled'
				 * * 'disabled'
				 */
				appState: AppState.LOADING
			}
		},
		methods: {
			getOwner (entityType, entityId) {
				let ownerSeed = {entityType, entityId}
				let response = axios.get(`${this.$baseApiUrl}/entity?entityType=${entityType}&entityId=${entityId}`).then(response => {
					this.owner = response.data.data
				});
			},

			applyConfig (configData) {
				Vue.set(this, 'config', configData);
				this.appState = this.config.appEnabled ? AppState.ENABLED : AppState.DISABLED;
			}
		},
		mounted () {
			var _this = this;

			axios
				.get(Vue.prototype.$baseApiUrl + '/app-config')
				.then(response => {
					_this.applyConfig(response.data.data);
				})
				.catch(error => {
					_this.appState = AppState.PROBLEM;
					_this.error.message = "There was a problem loading this page, please try again.";
					console.log(error);
				});

			EventBus.$on('update-owner', owner => this.owner = owner);

			// Get owner from the server
			EventBus.$on('get-owner', this.getOwner);

			EventBus.$on('error', error => this.error = error);
		},
		beforeCreate () {
			this.EventBus = EventBus;
			this.AppState = AppState;
		}
	}
</script>

<style lang="scss">
	@import url('https://fonts.googleapis.com/css?family=Roboto|Roboto+Condensed:300,400,700');

	@import '../assets/scss/var';
	@import '../assets/scss/mixin';
	@import '../assets/scss/reset';
	@import '../assets/scss/loading';

	$font-family-default : 'Roboto Condensed', Helvetica, Arial, sans-serif;

	// Default font sizing - i.e. mobile
	html {
		// Background COLOR shows through the gradient occasionally on Android phones (facepalm) so include it here
		background  : $color-ents24-boost-sea-green;
		font-family : $font-family-default;
		font-size   : 16px;
	}

	input, button {
		font-family : $font-family-default;
	}

	body {
		-moz-osx-font-smoothing : grayscale;
		-webkit-font-smoothing  : antialiased;
		color                   : $color-white;
		text-align              : center;
		background              : url(../assets/img/stars.png), url(../assets/img/green-shape.svg);
		background-repeat       : repeat, no-repeat;
		background-position     : top center, top center;
		background-size         : 180%, 1250px;
		background-color        : $color-ents24-boost-sea-green;
		line-height             : 2rem;

		@media (min-width: 600px) {
			background-size : 100%;
		}
	}

	// BIG CAPS HEADER FOR LANDING AND THANKS PAGES
	h1 {
		font-size      : 2.2rem;
		line-height    : 1;
		font-weight    : 300;
		text-transform : uppercase;
		margin         : 0 0 $gap-large 0;
	}

	// Tall-but-not-caps Header For Prominent Text On Each Page
	h2 {
		font-size      : 1.7rem;
		line-height    : 1;
		font-weight    : 300;
		text-transform : uppercase;
	}

	h3, figcaption {
		font-size      : 1.2rem;
	}

	// Which items get default margins?
	// XK: This setting provides consistent gutters everywhere e.g. between logo, TITLE and intro text
	// .. resist the urge to hard-code values elsewhere
	h2, p, figure, .logo, .logo-text, .with-v-gap {
		margin : 0 0 1.5rem 0;
	}

	// Most text content should be pushed away from the screen edge, but it's not a general rule so <body>
	// .. padding should be avoided. Put standard padding on common text elements
	h1, h2, h3, h4, h5, h6, p {
		padding : 0 $gap-default;
	}

	a {
		color : $color-ents24-sunset-salmon;
	}

	.text-dull {
		opacity: .5
	}

	.text-italic {
		font-style: italic
	}

	// Use these button styles for links and buttons in this app
	.button {
		background-color : transparent;
		border           : 2px solid $color-white;
		border-radius    : 1000px;
		color            : $color-white;
		cursor           : pointer;
		display          : inline-block;
		font-weight      : 500;
		line-height      : 35px;
		padding          : 10px;

		// Match border-width of gradient hoops below
		padding-left : 30px;
		padding-right : 30px;

		&.lightweight {
			border-width : 1px;
		}

		&:focus {
			background-color: $color-white-3;
		}

		font-size : 1rem;

		&.gradient {
			border-image  : url(../assets/img/border-image-primary.svg) 2 30 stretch;
			background    : transparent;
			border-style  : solid;
			border-width  : 2px 30px;
			color         : $color-white;
			display       : inline-block;
			padding-left  : 0;
			padding-right : 0;

			&.sunset {
				border-image : url(../assets/img/border-image-sunset.svg) 2 30 stretch;
			}

			&.large {
				border-width : 2px 30px;
				font-size    : 1.6rem;
			}

			&:enabled {
				&:hover, &:focus {
					color            : $color-white;
					background-color : $color-white-0;
				}
			}
		}

		&.gradient-filled {
			background-color : $color-primary;
			background-image : linear-gradient(135deg, $color-primary-gradient-from, $color-primary-gradient-to);
			border           : none;
			border-radius    : 1000px;
			color            : $color-black;
			cursor           : pointer;
			display          : inline-block;
			font-weight      : 500;
			line-height      : 35px;
			padding          : 0 20px;
			text-align       : center;
			text-decoration  : none;

			&.large {
				font-size : 1.6rem;
			}

			&.sunset {
				background-image : linear-gradient(135deg, $color-ents24-sunset-pink, $color-ents24-sunset-salmon);
			}
		}

		&.large {
			font-size : 1.4rem!important;
		}

		&.close {
			background      : transparent url(../assets/img/close.svg) center center no-repeat;
			background-size : cover;
			border          : none;
			border-radius   : 0;
			display         : block;
			padding         : 0;
			position        : absolute;

			// Sensible defaults (absolute, top right e.g. in a modal)
			width  : 20px;
			height : 20px;
			top    : $gap-default;
			right  : $gap-default;

			&.inline {
				display     : inline-block;
				height      : 1rem;
				margin      : 0;
				margin-left : $gap-default;
				position    : static;
				width       : 1rem;
			}

			&:focus {
				background-color : $color-white-3;
			}
		}

		&.back {
			background      : transparent url(../assets/img/left-chevron-white.svg) center center no-repeat;
			background-size : contain;
			border          : none;
			border-radius   : 0;
			position: absolute;
			width  : 20px;
			height : 26px;
			margin-right : $gap-default;
			padding : 0;


			&.inline {
				display  : inline-block;
				height   : 1rem;
				margin   : 0 $gap-default;
				position : static;
				width    : 1rem;
			}

			&:focus {
				background-color: $color-white-3;
			}
		}

		&.backward {
			display     : inline-flex;
			align-items : center;

			&:before {
				background   : transparent url(../assets/img/left-arrow-white.svg) center center / contain no-repeat;
				content      : '';
				height       : 25px;
				margin-right : $gap-default;
				width        : 30px;
			};
		}

		&.forward {
			display     : inline-flex;
			align-items : center;

			&:after {
				background  : transparent url(../assets/img/right-arrow-white.svg) center center / contain no-repeat;
				content     : '';
				height      : 25px;
				margin-left : $gap-default;
				width       : 30px;
			};
		}
	}

	.page {
		margin     : auto; // Centered on DT
		max-width  : 900px; // Arbitrary. Allows 3 packages side-by side at DT width
		min-height : 100vh;

		&.with-logo {
			padding-top : 10vw; // Allow logo to sink as device width grows; to match BG shape
		}
	}

	.logo {
		background : url(../assets/img/boost-buttons-logo.svg) top center / contain no-repeat;
		display    : block;
		height     : 220px;
	}

	.logo-text {
		display       : flex;
		height        : 50px;
		margin-bottom : 20px;
		justify-content: center;
		align-items: stretch;

		div {
			background : url(../assets/img/boost-buttons-logo-text.svg) top center / contain no-repeat;
			position   : relative;
			width      : 270px;

			@media (max-width: 350px) {
				width  : 230px;
			}

			.back {
				left     : -32px;
				margin   : 0;
				position : absolute;
				top      : 0px;
			}
		}
	}

	.loading {
		@include loading-spinner-quarter;
		margin : $gap-large;
	}

	.dull {
		opacity : .5;
	}

	.warning {
		// XK: removed salmon color - we're using that for links in places
	}

	// Centralised, appears on two pages
	.chosen-entity {
		background   : $color-black;
		box-sizing   : border-box;
		color        : $color-white;
		font-size    : 1.4rem;
		margin-left  : auto;
		margin-right : auto;
		max-width    : 95%; // Enforce a gap without compromising margin:auto
		padding      : 0 40px; // so long titles don't cover "X" close button
		position     : relative;

		&.with-close-button {
			border-radius : 30px;
			opacity       : .8;
			padding       : 10px 40px; // so long titles don't cover "X" close button

			button.close {
				right : 20px;
				top   : 20px;
			}
		}
	}

	// On desktop, increase body text and main headers
	@media only screen and (min-width: 600px) {
		html {
			font-size : 18px;
		}

		h1 {
			font-size   : 3rem;
		}
	}

	::-webkit-scrollbar {
		height : 6px;
		width  : 6px;
	}

	::-webkit-scrollbar-thumb {
		background: rgba(255,255,255,0.2);
	}

	// XK: `checkbox-radio()` is fairly verbose - output that mixin *once* and apply BG separately
	input[type="checkbox"],
	input[type="radio"] {
	  @include checkbox-radio()
	}

	input[type="checkbox"] {
	  @include checkbox()
	}

	input[type="radio"] {
	  @include radio()
	}

	.meerkat-enter-active, .meerkat-leave-active {
	  transition: transform .15s ease-out
	}

	// Start transition 100% lower down than the element's height
	.meerkat-enter, .meerkat-leave-to {
	  transform: translateY(100%)
	}

</style>
